import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Play, Square, Clock, History, ListChecks, Trash2, Pencil, Check, X } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser, getRealUser } from "../lib/auth.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import StopUkolDialog from "./StopUkolDialog.jsx";
import { toast } from "./Toast.jsx";
import UkolPicker from "./UkolPicker.jsx";

// === TimerWidget — stopky v horní liště (Toggl styl) ===
// Pro měřicí role (manager, csm, sales, nutritionist). Vedle zvonečku.
// Běžící měření: tikající čas + činnost přímo v liště. Klik = panel:
//   - start s přednastavenou činností dle role (+ kvadrant u manager/csm)
//   - "začal jsem už v HH:MM" max 2 hodiny zpět (backend hlídá, záznam dostane
//     štítek "zpětný start", ať je rozlišené měření od začátku vs. doplněné)
//   - stop
// Data sdílí s /api/time-report. Po akci pošle event "fw40-time-changed",
// aby se stránka Time report (pokud je otevřená) obnovila.

const TRACKING_ROLES = new Set(["manager", "csm", "sales", "nutritionist", "nutricni"]);
// Výběr úkolu z Operations mají jen manager a CSM (sales/nutritionist měří jen činnosti).
const TASK_LINK_ROLES = new Set(["manager", "csm"]);

const ACTIVITY_COLORS = {
  "Operations": "bg-plum-100 text-plum-700", "Marketing": "bg-pink-100 text-pink-700",
  "HR": "bg-blue-100 text-blue-700", "Clients Management": "bg-emerald-100 text-emerald-700",
  "Meeting": "bg-amber-100 text-amber-700", "Sales": "bg-rose-100 text-rose-700",
  "Tréninky": "bg-brand-100 text-brand-700", "Živé akce": "bg-orange-100 text-orange-700",
  "Education": "bg-sky-100 text-sky-700", "Administrativa": "bg-slate-200 text-slate-700",
  "Výživa a reporty": "bg-lime-100 text-lime-700", "Strategy": "bg-brand-100 text-brand-700",
};
const QX_DESC = { Q1: "Urgentní + důležité", Q2: "Důležité, neurgentní", Q3: "Urgentní, nedůležité", Q4: "Neurgentní, nedůležité" };

function fmtDur(sec) {
  sec = Math.max(0, Math.round(sec));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

export default function TimerWidget() {
  const role = String(getCurrentUser()?.role || "").toLowerCase();
  const realRole = String(getRealUser()?.role || "").toLowerCase();
  const enabled = TRACKING_ROLES.has(role);
  // Owner v "Náhled jako": backend běží pod ownerem → pošli roleHint, ať náhled
  // ukáže činnosti/pravidla dané role (Sales: Sales/Meeting/Education atd.).
  const roleHint = realRole === "owner" && role !== "owner" ? `?roleHint=${role}` : "";

  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [oblast, setOblast] = useState("");
  const [popis, setPopis] = useState("");
  const [qx, setQx] = useState("");
  const [backStart, setBackStart] = useState(false);
  const [minutesBack, setMinutesBack] = useState("");
  const [ukolId, setUkolId] = useState("");
  const [myTasks, setMyTasks] = useState([]);
  // Úprava běžícího měření (3.8.2026): činnost, kvadrant, úkol i popis za běhu.
  const [editRun, setEditRun] = useState(false);
  const [eOblast, setEOblast] = useState("");
  const [eQx, setEQx] = useState("");
  const [eUkolId, setEUkolId] = useState("");
  const [ePopis, setEPopis] = useState("");
  const [err, setErr] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [now, setNow] = useState(Date.now());
  // Stop s navazanym ukolem: dialog "hotovo?" — musi byt tady nahore, ne az za
  // `if (!enabled) return null` (React #310 — nestabilni poradi hooku).
  const [stopUkolDialog, setStopUkolDialog] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await apiFetch(`/api/time-report${roleHint}`);
      if (r.ok) setData(await r.json());
    } catch { /* widget nesmí shodit appku */ }
  }, [enabled, roleHint]);

  // Moje otevřené úkoly z Operations — co blok měření, to jeden úkol. Jen manager/csm.
  const canLinkTasks = TASK_LINK_ROLES.has(role);
  const loadTasks = useCallback(async () => {
    if (!enabled || !canLinkTasks) return;
    try {
      const r = await apiFetch("/api/operations-ukoly?mine=1");
      if (!r.ok) return;
      const j = await r.json();
      setMyTasks((j.tasks || []).filter((t) => ["zasobnik", "prioritni", "mesicni", "tydenni", "v_procesu"].includes(t.status)));
    } catch { /* volitelná featura, nesmí shodit widget */ }
  }, [enabled, canLinkTasks]);

  useEffect(() => { load(); }, [load]);
  // Refresh každé 2 min + když stránka Time report něco změní.
  useEffect(() => {
    if (!enabled) return;
    const iv = setInterval(load, 2 * 60 * 1000);
    const onChanged = () => load();
    window.addEventListener("fw40-time-changed", onChanged);
    return () => { clearInterval(iv); window.removeEventListener("fw40-time-changed", onChanged); };
  }, [enabled, load]);

  const running = data?.running || null;
  // Tikání jen když běží měření.
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [running]);

  // Zavřít panel po kliku mimo
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      const vTlacitku = ref.current && ref.current.contains(e.target);
      const vPanelu = panelRef.current && panelRef.current.contains(e.target);
      // Nabídka výběru úkolu (UkolPicker) je v portálu mimo panel, klik do ní panel nezavírá.
      const vNabidce = e.target instanceof Element && !!e.target.closest("[data-plovouci-nabidka]");
      if (!vTlacitku && !vPanelu && !vNabidce) setOpen(false);
    };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", esc); };
  }, [open]);

  // Na mobilu je panel spodní sheet přes celou šířku — zamkni scroll pozadí,
  // ať se stránka pod sheetem nehýbe. Na desktopu (sm+) je to dropdown, tam nic.
  useEffect(() => {
    if (!open || typeof window === "undefined" || window.innerWidth >= 640) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const activities = data?.activities || [];
  const useQuadrants = data?.useQuadrants;
  const elapsedSec = running ? (now - new Date(running.start_at).getTime()) / 1000 : 0;

  const post = async (payload) => {
    setBusy(true); setErr("");
    try {
      const r = await apiFetch("/api/time-report", { method: "POST", body: JSON.stringify(payload) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Akce selhala");
      await load();
      window.dispatchEvent(new Event("fw40-time-changed"));
      return true;
    } catch (e) { setErr(e.message); return false; }
    finally { setBusy(false); }
  };

  // Otevře edit běžícího měření s předvyplněnými aktuálními hodnotami.
  const openEditRun = () => {
    if (!running) return;
    setEOblast(running.oblast || "");
    setEQx(running.qx || "");
    setEUkolId(running.ukol_id || "");
    setEPopis(running.popis || "");
    setErr("");
    setEditRun(true);
  };
  const eAutoQx = activities.find((a) => a.key === eOblast)?.defaultQx || "";
  const saveEditRun = async () => {
    if (!eOblast) { setErr("Vyber činnost"); return; }
    const payload = { action: "updateRunning", oblast: eOblast, popis: ePopis, ukol_id: eUkolId || null };
    if (eUkolId) payload.ukol_nazev = myTasks.find((t) => t.id === eUkolId)?.nazev || "";
    if (useQuadrants && eQx) payload.qx = eQx;
    const ok = await post(payload);
    if (ok) setEditRun(false);
  };

  const start = async () => {
    if (!oblast) { setErr("Vyber činnost"); return; }
    const payload = { action: "start", oblast, popis };
    // Owner v náhledu: backend validuje činnosti dle náhledové role.
    if (roleHint) payload.roleHint = role;
    if (useQuadrants && qx) payload.qx = qx;
    if (backStart && parseInt(minutesBack, 10) > 0) payload.minutesBack = parseInt(minutesBack, 10);
    if (ukolId) {
      payload.ukol_id = ukolId;
      payload.ukol_nazev = myTasks.find((t) => t.id === ukolId)?.nazev || "";
    }
    const ok = await post(payload);
    if (ok) { setPopis(""); setQx(""); setBackStart(false); setMinutesBack(""); setUkolId(""); setOpen(false); }
  };
  // Stop s navázaným úkolem: dialog "hotovo?" — stejné chování jako na stránce
  // Time report. Ano → hotovo přes operations-ukoly (platí všechna pravidla),
  // Ne → backend vrátí úkol z Procesu do původního sloupce.
  const stop = () => {
    // Panel/sheet zavřít — dialog i výsledek se stejně zobrazí přes celou obrazovku
    // (na mobilu by sheet zůstal viset pod dialogem).
    setOpen(false);
    if (running?.ukol_id) setStopUkolDialog(true);
    else post({ action: "stop" });
  };
  const doStopWithUkol = async (markDone) => {
    setStopUkolDialog(false);
    if (markDone && running?.ukol_id) {
      try {
        const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ id: running.ukol_id, status: "hotovo" }) });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j.keKontrole) toast(j.message || "Úkol má štítek kontrola - odevzdáno zadavateli ke schválení (dostal zprávu ve Slacku).");
        if (!r.ok) setErr(`Úkol nejde označit hotový: ${j.error || "chyba"}. Vrátí se do původního sloupce.`);
      } catch (e) { setErr(`Úkol nejde označit hotový: ${e?.message || e}.`); }
    }
    await post({ action: "stop" });
  };
  // Zrušení přes aplikační ConfirmDialog (ne nativní window.confirm), ať ladí s designem.
  const discard = () => { setOpen(false); setConfirmDiscard(true); };
  const doDiscard = async () => {
    setConfirmDiscard(false);
    await post({ action: "discard" });
  };

  // Panel žije v portálu u body, proto si pozici na široké obrazovce spočítáme
  // z tlačítka sami — `sm:top-full` by se vztahovalo k body, ne k liště.
  const panelRef = useRef(null);
  const [siroka, setSiroka] = useState(() => (typeof window === "undefined" ? true : window.innerWidth >= 640));
  const [panelStyl, setPanelStyl] = useState(undefined);
  // Bezici mereni ma v panelu jen cas a tri tlacitka, formular pro spusteni je
  // naopak dlouhy. Proto se panel na telefonu roztahne jen v druhem pripade.
  const kompaktniPanel = !!running && !editRun;
  useEffect(() => {
    if (!open) return;
    const preposicuj = () => {
      const jeSiroka = window.innerWidth >= 640;
      setSiroka(jeSiroka);
      const tl = ref.current && ref.current.querySelector("button");
      const r = tl ? tl.getBoundingClientRect() : null;
      if (jeSiroka && r) {
        setPanelStyl({ top: Math.round(r.bottom + 8), right: Math.round(window.innerWidth - r.right) });
      } else if (kompaktniPanel) {
        // 12.9.2026: kdyz mereni bezi, je karta vysoka podle obsahu. Drive sahala
        // od listy ke spodku obrazovky a pod tlacitky zustalo prazdno.
        setPanelStyl({
          top: "calc(env(safe-area-inset-top) + 8px)",
          maxHeight: "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 16px)",
          left: 8, right: 8,
        });
      } else {
        // Telefon: velká karta hned pod horní lištou, přes skoro celou obrazovku,
        // ne úzký proužek u spodního okraje. Výška končí nad spodní navigací,
        // ať jsou Start / Uložit vždy vidět a dá se v panelu rolovat.
        setPanelStyl({
          top: "calc(env(safe-area-inset-top) + 8px)",
          bottom: "calc(env(safe-area-inset-bottom) + 8px)",
          left: 8, right: 8,
        });
      }
    };
    preposicuj();
    window.addEventListener("resize", preposicuj);
    window.addEventListener("scroll", preposicuj, true);
    return () => {
      window.removeEventListener("resize", preposicuj);
      window.removeEventListener("scroll", preposicuj, true);
    };
  }, [open, kompaktniPanel]);

  // 12.9.2026: dřív byl `if (!enabled) return null` výš, ale hooky pro pozici
  // panelu (8.9.) přibyly až pod ním. Když se `enabled` přepnulo z ne na ano
  // (načtení role po přihlášení, Náhled jako), změnil se počet hooků a React
  // spadl chybou #310. Podmíněný návrat musí být až za posledním hookem.
  if (!enabled) return null;

  const selectedAct = activities.find((a) => a.key === oblast);
  const effectiveQx = qx || selectedAct?.defaultQx || "";

  return (
    <div className="relative" ref={ref}>
      {/* Tlačítko v liště */}
      {/* 12.9.2026: stopky byly orámovaný čip, jediný takový prvek v liště
          vedle náhledu. Teď je to obyčejná ikona 44 px jako zvoneček a chat.
          Když měření běží, na místě ikony svítí zelený čas. */}
      <button
        onClick={() => { setOpen((o) => !o); if (!open) { load(); loadTasks(); } }}
        className={`flex h-10 sm:h-11 items-center justify-center gap-1.5 rounded-xl transition-colors ${
          running ? "bg-emerald-50 px-2 sm:px-2.5 text-emerald-700" : "w-10 sm:w-11 text-slate-600 sm:hover:bg-slate-100"
        }`}
        title={running ? `Měřím: ${running.oblast}` : "Spustit měření času"}
        aria-label="Stopky"
      >
        {running ? (
          <>
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            <span className="text-xs font-bold tabular-nums">{fmtDur(elapsedSec)}</span>
            <span className="hidden max-w-[110px] truncate text-xs font-semibold md:inline">{running.oblast}</span>
          </>
        ) : (
          <Clock className="h-6 w-6" />
        )}
      </button>

      {/* Panel — na mobilu spodní sheet přes celou šířku, od sm výš dropdown
          u tlačítka.
          8.9.2026 (hlášení Michala „Time tracking nejde spustit"): panel se na
          telefonu kreslil NAHOŘE a schovaný pod stavovým řádkem, takže Start
          nešel zmáčknout. Horní lišta má backdrop-blur a ten dělá z předka
          containing block pro `position: fixed` — `bottom-0` se pak počítalo ke
          spodku lišty, ne k okraji obrazovky. Řeší to portál do body (stejný
          důvod i stejné řešení jako u spodní lišty v Time trackingu), pozice se
          dopočítá z tlačítka. Na telefonu je panel velká karta pod lištou přes
          skoro celou obrazovku, ne nízký proužek u spodního okraje. */}
      {open && ReactDOM.createPortal(
        <>
        <div className="sm:hidden fixed inset-0 bg-brand-dark/50 z-[60] backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" />
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Stopky"
          style={panelStyl}
          className={siroka
            ? "fixed z-[61] w-80 max-w-[calc(100vw-2rem)] overflow-visible rounded-xl border border-slate-200 bg-white shadow-2xl p-3"
            : "fixed z-[61] flex flex-col overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white shadow-2xl p-4"}>
          {/* 12.9.2026: panel dostal tmavou hlavičku ze vzorníku, stejnou jako
              karty jinde v aplikaci. Křížek je v ní, ne volně nad obsahem. */}
          <div className="-mx-4 -mt-4 mb-3 flex items-center gap-3 rounded-t-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-white sm:-mx-3 sm:-mt-3 sm:mb-2.5 sm:rounded-t-xl sm:px-3 sm:py-2.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 sm:h-9 sm:w-9">
              <Clock className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-serif text-lg font-bold leading-tight sm:text-base">{running ? "Právě běží" : "Stopky"}</span>
              <span className="block truncate text-xs text-white/70">
                {running ? `${running.oblast}${running.qx ? `, kvadrant ${running.qx}` : ""}` : "Změř si čas na práci"}
              </span>
            </span>
            <button onClick={() => setOpen(false)} aria-label="Zavřít"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 sm:hover:bg-white/20">
              <X className="h-5 w-5" />
            </button>
          </div>
          {err && <div className="mb-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-2 py-1.5">{err}</div>}
          {running ? (
            <div className="space-y-3">
              <div className="text-center">
                <div className="font-serif text-4xl font-bold tabular-nums text-slate-900 sm:text-3xl">{fmtDur(elapsedSec)}</div>
                <div className="mt-2 flex items-center justify-center gap-1.5 flex-wrap">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ACTIVITY_COLORS[running.oblast] || "bg-slate-100 text-slate-700"}`}>{running.oblast}</span>
                  {running.qx && <span className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5" title={QX_DESC[running.qx]}>{running.qx}</span>}
                  {running.puvod === "zpetny_start" && (
                    <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 rounded px-1.5 py-0.5" title="Začátek vložen zpětně">zpětný start</span>
                  )}
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                    od {new Date(running.start_at).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                {running.ukol_nazev && (
                  <div className="text-xs text-brand-700 mt-1 inline-flex items-center gap-1 max-w-full">
                    <ListChecks className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{running.ukol_nazev}</span>
                  </div>
                )}
                {running.popis && <div className="text-xs text-slate-500 mt-1 truncate">{running.popis}</div>}
              </div>

              {!editRun ? (
                <>
                  {/* 12.9.2026 (rozhodnutí Č2): tři stejně široká tlačítka pod sebou.
                      Zahození bylo drobný odkaz, na telefonu se do něj nedalo trefit. */}
                  <div className="space-y-2">
                    <button onClick={stop} disabled={busy} className="btn btn-danger w-full">
                      <Square className="h-5 w-5" /> Zastavit a uložit
                    </button>
                    <button onClick={openEditRun} disabled={busy}
                      title="Změnit činnost, kvadrant, úkol nebo poznámku běžícího měření, čas běží dál."
                      className="btn btn-secondary w-full">
                      <Pencil className="h-5 w-5" /> Upravit měření
                    </button>
                    <button onClick={discard} disabled={busy}
                      className="btn w-full border border-rose-200 bg-rose-50 text-rose-700 sm:hover:bg-rose-100">
                      <Trash2 className="h-5 w-5" /> Zahodit bez uložení
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-2.5 border-t border-slate-100 pt-2.5">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Upravit běžící měření</div>
                  <div className="flex flex-wrap gap-1.5">
                    {activities.map((a) => (
                      <button key={a.key} type="button" onClick={() => { setEOblast(a.key); setEQx(""); }}
                        className={`text-sm px-4 py-2.5 sm:text-xs sm:px-2.5 sm:py-1 font-semibold rounded-full border transition ${
                          eOblast === a.key
                            ? (ACTIVITY_COLORS[a.key] || "bg-slate-800 text-white") + " border-transparent ring-2 ring-slate-300"
                            : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                        }`}>
                        {a.key}
                      </button>
                    ))}
                  </div>
                  {useQuadrants && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] text-slate-400 font-semibold">Kvadrant:</span>
                      {["Q1", "Q2", "Q3", "Q4"].map((x) => (
                        <button key={x} type="button" onClick={() => setEQx(x)}
                          title={QX_DESC[x]}
                          className={`text-sm px-3.5 py-2 sm:text-[11px] sm:px-2 sm:py-0.5 font-bold rounded border ${
                            (eQx || eAutoQx) === x ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"
                          }`}>
                          {x}
                        </button>
                      ))}
                      {!eQx && <span className="text-[11px] text-slate-400">(auto)</span>}
                    </div>
                  )}
                  {canLinkTasks && myTasks.length > 0 && (
                    <UkolPicker tasks={myTasks} value={eUkolId} onChange={setEUkolId} placeholder="Úkol z Operations (volitelně)…" className="w-full" />
                  )}
                  <input value={ePopis} onChange={(e) => setEPopis(e.target.value)} placeholder="Na čem děláš (volitelně)…"
                    className="field w-full" />
                  <div className="flex gap-2">
                    <button onClick={saveEditRun} disabled={busy || !eOblast}
                      className="btn btn-primary flex-1">
                      <Check className="w-4 h-4" /> Uložit
                    </button>
                    <button onClick={() => setEditRun(false)} disabled={busy}
                      className="inline-flex items-center justify-center border border-slate-300 text-slate-600 hover:bg-slate-50 font-semibold px-3.5 py-3.5 sm:py-2.5 rounded-xl text-base sm:text-sm">
                      Zrušit
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2.5 flex flex-col flex-1">
              <div className="flex flex-wrap gap-1.5">
                {activities.map((a) => (
                  <button key={a.key} type="button" onClick={() => { setOblast(a.key); setQx(""); setErr(""); }}
                    className={`text-sm px-4 py-2.5 sm:text-xs sm:px-2.5 sm:py-1 font-semibold rounded-full border transition ${
                      oblast === a.key
                        ? (ACTIVITY_COLORS[a.key] || "bg-slate-800 text-white") + " border-transparent ring-2 ring-slate-300"
                        : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                    }`}>
                    {a.key}
                  </button>
                ))}
              </div>
              {useQuadrants && oblast && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] text-slate-400 font-semibold">Kvadrant:</span>
                  {["Q1", "Q2", "Q3", "Q4"].map((x) => (
                    <button key={x} type="button" onClick={() => setQx(x === effectiveQx && qx ? "" : x)}
                      title={QX_DESC[x]}
                      className={`text-sm px-3.5 py-2 sm:text-[11px] sm:px-2 sm:py-0.5 font-bold rounded border ${
                        effectiveQx === x ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"
                      }`}>
                      {x}
                    </button>
                  ))}
                  {!qx && selectedAct?.defaultQx && <span className="text-[11px] text-slate-400">(auto)</span>}
                </div>
              )}
              {canLinkTasks && myTasks.length > 0 && (
                <UkolPicker tasks={myTasks} value={ukolId} onChange={setUkolId} placeholder="Úkol z Operations (volitelně)…" className="w-full" />
              )}
              <input value={popis} onChange={(e) => setPopis(e.target.value)} placeholder="Na čem děláš (volitelně)…"
                className="field w-full sm:text-sm sm:py-2" />
              <label className="flex items-center gap-2 py-1.5 sm:py-0 text-sm sm:text-xs text-slate-600 cursor-pointer select-none">
                <input type="checkbox" checked={backStart} onChange={(e) => setBackStart(e.target.checked)} className="w-5 h-5 sm:w-3.5 sm:h-3.5 accent-brand-600" />
                <History className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-slate-400" /> Začal(a) jsem už dřív
              </label>
              {backStart && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2">
                  <span className="text-xs font-semibold text-amber-800">před</span>
                  <input type="number" min="1" max="120" value={minutesBack} onChange={(e) => setMinutesBack(e.target.value)}
                    placeholder="30" className="field w-16 text-center" />
                  <span className="text-xs font-semibold text-amber-800">minutami (max 120)</span>
                </div>
              )}
              {/* Na telefonu se Start odsune k dolní hraně, ať je pod palcem. */}
              <div className="flex-1 sm:hidden" />
              <button onClick={start} disabled={busy || !oblast}
                className="btn btn-primary w-full">
                <Play className="w-5 h-5 sm:w-4 sm:h-4" /> Start
              </button>
            </div>
          )}
          {/* Rezerva na konci rolovacího obsahu, ať poslední tlačítko nekončí na hraně. */}
          <div className="h-2 sm:hidden" />
        </div>
        </>, document.body)}
      {/* Dialog při zastavení měření s navázaným úkolem: checklist podúkolů
          přímo v dialogu, "Hotovo" se odemkne až po odškrtnutí všech. */}
      <StopUkolDialog
        open={stopUkolDialog}
        ukolId={running?.ukol_id}
        ukolNazev={running?.ukol_nazev}
        busy={busy}
        onDone={() => doStopWithUkol(true)}
        onNotDone={() => doStopWithUkol(false)}
        onClose={() => setStopUkolDialog(false)}
      />
      <ConfirmDialog
        open={confirmDiscard}
        title="Zrušit běžící měření?"
        description="Rozdělané měření se zahodí bez uložení. Nejde to vrátit."
        confirmLabel="Zrušit měření"
        variant="danger"
        onConfirm={doDiscard}
        onCancel={() => setConfirmDiscard(false)}
      />
    </div>
  );
}
